import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { safeJsonParse } from '../../../../utils/safeJsonParse';
import { formatFilterText } from '../../../../utils/formatFilterText';
import {
  resetFilters,
  setCurrentTab,
  setCurrentTabName,
  setSkipFilterReset,
} from '../../../../redux/slices/currentTabSlice';
import { setFilterBValueWithUpdate } from '../../../../redux/actions/filterActions';
import { setTabOrPublicationPage } from '../../../../redux/slices/appSlice';
import { selectCartItemsCount } from '../../../../redux/slices/cartSlice';
import styles from './index.module.scss';

const parseTabTags = (tabTag) => {
  if (!tabTag) return [];
  if (String(tabTag).includes(',')) {
    return String(tabTag)
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  return [String(tabTag).trim()];
};

const getFiltersForSection = (tabs, sectionName) => {
  const sectionTab = tabs.find((tab) => tab.eng_name === sectionName);
  if (!sectionTab?.publications) return [];

  let allFilterBValues = ['все'];

  sectionTab.publications.forEach((publication) => {
    if (!publication.publication_settings) return;

    const settingsRaw = String(publication.publication_settings).trim();
    const isJsonString = settingsRaw.startsWith('{') || settingsRaw.startsWith('[');
    if (!isJsonString) return;

    try {
      const obj = safeJsonParse(publication.publication_settings, {});
      if (!obj.publication_filter_b) return;

      const filterBValues = Array.isArray(obj.publication_filter_b)
        ? obj.publication_filter_b
        : [obj.publication_filter_b];

      allFilterBValues = [...allFilterBValues, ...filterBValues];
    } catch {
      // ignore invalid publication settings
    }
  });

  return [...new Set(allFilterBValues)];
};

const getSectionPath = (engName) => {
  if (!engName || engName === 'main_tab') return '/';
  return `/${engName}`;
};

const SiteSections = ({ publication, globalIndex }) => {
  void globalIndex;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tabsData = useSelector((state) => state.tabs_data?.tabs ?? []);
  const showName = useSelector((state) => state.currentTab.show_name);
  const cartItemsCount = useSelector(selectCartItemsCount);

  const sectionTitle = React.useMemo(() => {
    if (!publication?.content) return '';

    try {
      const parsed = JSON.parse(publication.content);
      const blockContent = Array.isArray(parsed?.[0]?.block_content) ? parsed[0].block_content : [];
      const rawTitle = blockContent[0]?.content_text ?? blockContent[0]?.cotent_text ?? '';
      const title = String(rawTitle || '').trim();
      if (!title || title.toLowerCase() === 'none') return '';
      return title;
    } catch {
      return '';
    }
  }, [publication?.content]);

  const sections = React.useMemo(() => {
    if (!Array.isArray(tabsData) || tabsData.length === 0) return [];

    return tabsData
      .map((tab) => {
        const tagArray = parseTabTags(tab.tab_tag);
        const filters = getFiltersForSection(tabsData, tab.eng_name);
        return {
          ...tab,
          tagArray,
          filters,
          hasHeaderCart: tagArray.includes('header_cart'),
        };
      })
      .filter((tab) => tab.tagArray.includes('in_section_block'));
  }, [tabsData]);

  const handleSectionClick = (tab) => {
    dispatch(resetFilters());
    dispatch(setTabOrPublicationPage('tab'));
    dispatch(setCurrentTab(tab));
    dispatch(setCurrentTabName(tab.eng_name));
  };

  const handleFilterClick = (sectionName, filterValue) => {
    const targetSection = tabsData.find((tab) => tab.eng_name === sectionName);
    if (!targetSection) return;

    dispatch(setSkipFilterReset(true));
    dispatch(setFilterBValueWithUpdate(filterValue));

    window.setTimeout(() => {
      navigate(getSectionPath(targetSection.eng_name));
      dispatch(setCurrentTab(targetSection));
      dispatch(setCurrentTabName(targetSection.eng_name));
      dispatch(setTabOrPublicationPage('tab'));

      window.setTimeout(() => {
        dispatch(setSkipFilterReset(false));
      }, 200);
    }, 50);
  };

  if (sections.length === 0) return null;

  const headerTitle =
    sectionTitle || publication?.show_name || publication?.publication_name || 'Разделы сайта';

  return (
    <section className={styles.siteSections} aria-label='Разделы сайта'>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{headerTitle}</h2>
      </div>

      <ul className={styles.sectionsList}>
        {sections.map((tab) => {
          const isActive = showName === tab.show_name;
          const sectionLabel = tab.show_name || tab.tab_name || tab.eng_name || 'Раздел';
          const showFilters = tab.filters.length > 1;

          return (
            <li key={tab.eng_name || tab.id || tab.show_name} className={styles.sectionItem}>
              <Link
                to={getSectionPath(tab.eng_name)}
                className={`${styles.sectionLink} ${isActive ? styles.sectionLinkActive : ''}`}
                onClick={() => handleSectionClick(tab)}>
                <span className={styles.sectionName}>{sectionLabel}</span>
                {tab.hasHeaderCart && cartItemsCount > 0 && (
                  <span className={styles.cartBadge}>{cartItemsCount}</span>
                )}
              </Link>

              {showFilters && (
                <div className={styles.filtersRow}>
                  {tab.filters.map((filterValue) => (
                    <button
                      key={`${tab.eng_name}-${filterValue}`}
                      type='button'
                      className={styles.filterChip}
                      onClick={() => handleFilterClick(tab.eng_name, filterValue)}>
                      {formatFilterText(filterValue)}
                    </button>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default SiteSections;
